import { Game } from '../../features/game/types/Game'
import { setAppAnalyst } from '../features/games/helpers/editGameHelpers'
import { getGameListSearchBody } from '../features/games/helpers/gameListHelpers'
import { GameAnalystMap, GameAnalystMapSetting, InternalGameDocument, InternalGameMutation } from '../features/games/types/InternalGame'
import { customError, optionalJson } from './helpers'
import { internalApiserverApi } from './internal-apiserver'

const pinnedGamesUrl = 'users/settings/internal_pinned_games'
const gameAnalystMapUrl = 'i/settings/apiserver/game_analyst_map'
const gameDocumentRequest = (id: string) => ({ url: `i/games/${encodeURIComponent(id)}`, params: { gameOnly: true, marketIso: 'us' } })
const gamesApi = internalApiserverApi.enhanceEndpoints({ addTagTypes: ['internalGameDocument', 'internalPinnedGames', 'gameAnalystMap'] }).injectEndpoints({
  endpoints: (builder) => ({
    // At most 20 whole games; `include` is ignored, and a market doesn't limit the answer to that market.
    searchInternalGameList: builder.query<Game[], { term: string; marketIso: string }>({
      query: ({ term, marketIso }) => ({ url: 'i/games/search', method: 'POST', body: getGameListSearchBody(term, marketIso) }),
      transformResponse: (games: Game[]) => games.map((game) => new Game(game)),
    }),
    // Takes a game id or an app id. The raw document, as a save must send it back. Tagged by the game id too, so a
    // write by game id refreshes a document read by app id.
    getInternalGameDocument: builder.query<InternalGameDocument, string>({
      query: gameDocumentRequest,
      transformResponse: (response: { game: InternalGameDocument }) => response.game,
      providesTags: (result, _error, id) => [
        { type: 'internalGameDocument', id },
        ...(result?.id && result.id !== id ? [{ type: 'internalGameDocument' as const, id: result.id }] : []),
      ],
    }),
    // The PUT answer is a market-resolved shape that can't be compared with the body, so the game is read again.
    saveInternalGame: builder.mutation<InternalGameDocument, InternalGameMutation>({
      async queryFn(game, _api, _extraOptions, baseQuery) {
        const saved = await baseQuery(game.id ? { url: `i/games/${game.id}`, method: 'PUT', body: game } : { url: 'i/games', method: 'POST', body: game })
        if (saved.error) return { error: saved.error }
        const answer = saved.data as { id?: string; game?: { id?: string } } | null
        const id = game.id ?? answer?.game?.id ?? answer?.id
        if (!id) return { error: customError('The saved game has no id') }
        const stored = await baseQuery(gameDocumentRequest(id))
        if (stored.error) return { error: stored.error }
        return { data: (stored.data as { game: InternalGameDocument }).game }
      },
      invalidatesTags: (result) => (result?.id ? [{ type: 'internalGameDocument', id: result.id }] : []),
    }),
    // Not the search: the page searches the game's name to confirm the delete, then refetches its own search.
    deleteInternalGame: builder.mutation<string, string>({
      query: (id) => ({ url: `i/games/${id}`, method: 'DELETE', responseHandler: 'text' }),
      invalidatesTags: (_result, _error, id) => [{ type: 'internalGameDocument', id }],
    }),
    // An unset setting answers 200 with an empty body.
    getInternalPinnedGames: builder.query<string[], void>({
      query: () => ({ url: pinnedGamesUrl, responseHandler: optionalJson }),
      transformResponse: (ids: string[] | null) => (Array.isArray(ids) ? ids : []),
      providesTags: ['internalPinnedGames'],
    }),
    // Sends the whole list, as legacy. Returns the list as stored afterwards.
    saveInternalPinnedGames: builder.mutation<string[], string[]>({
      async queryFn(ids, _api, _extraOptions, baseQuery) {
        const saved = await baseQuery({ url: pinnedGamesUrl, method: 'PUT', body: ids, responseHandler: 'text' })
        if (saved.error) return { error: saved.error }
        const stored = await baseQuery({ url: pinnedGamesUrl, responseHandler: optionalJson })
        if (stored.error) return { error: stored.error }
        return { data: Array.isArray(stored.data) ? (stored.data as string[]) : [] }
      },
      async onQueryStarted(_ids, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          dispatch(gamesApi.util.upsertQueryData('getInternalPinnedGames', undefined, data))
        } catch {}
      },
    }),
    // App id → analyst id.
    getGameAnalystMap: builder.query<GameAnalystMap, void>({
      query: () => ({ url: gameAnalystMapUrl, responseHandler: optionalJson }),
      transformResponse: (setting: GameAnalystMapSetting | null) => setting?.data ?? {},
      providesTags: ['gameAnalystMap'],
    }),
    // `PUT i/settings` replaces the whole map, so it's read just before the write and only one app is changed.
    // Returns the map as stored afterwards.
    saveGameAnalyst: builder.mutation<GameAnalystMap, { appId: string; analystId: string }>({
      async queryFn({ appId, analystId }, _api, _extraOptions, baseQuery) {
        const current = await baseQuery({ url: gameAnalystMapUrl, responseHandler: optionalJson })
        if (current.error) return { error: current.error }
        const data = setAppAnalyst((current.data as GameAnalystMapSetting | null)?.data ?? {}, appId, analystId)
        const saved = await baseQuery({ url: 'i/settings', method: 'PUT', body: { name: 'game_analyst_map', data }, responseHandler: 'text' })
        if (saved.error) return { error: saved.error }
        const stored = await baseQuery({ url: gameAnalystMapUrl, responseHandler: optionalJson })
        if (stored.error) return { error: stored.error }
        return { data: (stored.data as GameAnalystMapSetting | null)?.data ?? {} }
      },
      async onQueryStarted(_args, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          dispatch(gamesApi.util.upsertQueryData('getGameAnalystMap', undefined, data))
        } catch {}
      },
    }),
  }),
})

export const {
  useSearchInternalGameListQuery,
  useLazySearchInternalGameListQuery,
  useGetInternalGameDocumentQuery,
  useLazyGetInternalGameDocumentQuery,
  useSaveInternalGameMutation,
  useDeleteInternalGameMutation,
  useGetInternalPinnedGamesQuery,
  useSaveInternalPinnedGamesMutation,
  useGetGameAnalystMapQuery,
  useSaveGameAnalystMutation,
} = gamesApi
